import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import customerService from '../../services/customerService';
import clientCategoryService from '../../services/clientCategoryService';
import { IcCreditCard, IcUser, IcUserPlus, IcSave, IcTrash, IcAlertCircle } from '../../theme/icons';
import { BtnSk } from '../../components/Skeleton';

// Mêmes recettes que /products/add (owner 2026-09-09) : champ `.inp` 13.5px
// py-2.5, libellé 12px 500 encre mb-1.5, aide 11px gris, suffixe DT gris à
// droite du champ ; erreur = .inp-err + message .form-err.
const inputClass = 'inp w-full px-3 py-2.5 text-sm text-gray-900';
const inputErrClass = 'inp inp-err w-full px-3 py-2.5 text-sm text-gray-900';
const labelClass = 'block text-xs font-medium text-gray-900 mb-1.5';
const helpClass = 'text-[11px] text-gray-400 mt-1.5';
const suffixClass = 'absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none';

const AddCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    card_number: '',
    points: '',
    credit: '',
    credit_maximum: '',
    score: '',
    category_id: '',
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Valeurs telles que chargées à l'ouverture de la fiche. Le crédit et les
  // points bougent tout seuls (vente à crédit, acompte, fidélité) pendant que
  // la fiche est ouverte : les renvoyer tels quels effacerait ces mouvements.
  const loadedRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await clientCategoryService.getAllCategories();
        const categoriesArray = categoriesData.results ? categoriesData.results : (Array.isArray(categoriesData) ? categoriesData : []);
        setCategories(categoriesArray);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();

    if (isEditMode) {
      const fetchCustomerData = async () => {
        try {
          setLoading(true);
          const customerData = await customerService.getCustomerById(id);
          const loaded = {
            first_name: customerData.first_name || '',
            last_name: customerData.last_name || '',
            email: customerData.email || '',
            phone: customerData.phone || '',
            address: customerData.address || '',
            card_number: customerData.card_number || '',
            points: customerData.points !== undefined && customerData.points !== null ? customerData.points : '',
            credit: customerData.credit !== undefined && customerData.credit !== null ? customerData.credit : '',
            credit_maximum: customerData.credit_maximum !== undefined && customerData.credit_maximum !== null ? customerData.credit_maximum : '',
            score: customerData.score !== undefined && customerData.score !== null ? customerData.score : '',
            category_id: customerData.category || '',
          };
          loadedRef.current = loaded;
          setFormData(loaded);
          setError(null);
        } catch (err) {
          console.error('Error fetching customer data:', err);
          setError('Impossible de récupérer les données du client. Veuillez réessayer.');
        } finally {
          setLoading(false);
        }
      };

      fetchCustomerData();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name.trim()) {
      setError('Le prénom du client est requis.');
      return;
    }

    // A loyalty member (FIDÈLE / FIDÈLE GOLD) must have a phone number and a
    // credit ceiling — same rule as the backend serializer.
    if (isLoyaltyCategory) {
      const errors = {};
      if (!formData.phone.trim()) {
        errors.phone = 'Le numéro de téléphone est obligatoire pour un client FIDÈLE / FIDÈLE GOLD.';
      }
      if (!(parseFloat(formData.credit_maximum) > 0)) {
        errors.credit_maximum = 'La limite de crédit est obligatoire pour un client FIDÈLE / FIDÈLE GOLD.';
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Veuillez compléter les champs obligatoires de la carte fidélité.');
        const firstInvalid = document.getElementById(errors.phone ? 'phone' : 'credit_maximum');
        if (firstInvalid) firstInvalid.focus();
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const num = (v) => (v === '' || v === null || v === undefined ? 0 : v);
      const customerData = {
        ...formData,
        card_number: (formData.card_number && formData.card_number.trim()) || null,
        score: num(formData.score),
        points: num(formData.points),
        credit: num(formData.credit),
        credit_maximum: num(formData.credit_maximum),
        category_id: formData.category_id || null,
      };

      if (isEditMode) {
        // N'envoyer que les champs réellement modifiés : corriger un téléphone
        // ne doit plus réécrire le crédit (ce qui, via reconcile_sales_credit,
        // réécrivait aussi les ventes du client).
        const base = loadedRef.current || {};
        const same = (a, b) => String(a ?? '') === String(b ?? '');
        const patch = {};
        Object.keys(formData).forEach((key) => {
          if (!same(formData[key], base[key])) patch[key] = customerData[key];
        });
        // Le crédit ne part qu'accompagné de la valeur lue à l'ouverture : le
        // serveur refuse alors l'écriture si une vente à crédit ou un acompte
        // est passé entre-temps.
        if ('credit' in patch) patch.expected_credit = num(base.credit);
        if (Object.keys(patch).length > 0) {
          await customerService.patchCustomer(id, patch);
        }
      } else {
        await customerService.createCustomer(customerData);
      }

      navigate('/clients');
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} customer:`, err);
      // Le crédit a bougé pendant que la fiche était ouverte : le serveur a
      // refusé l'écriture plutôt que d'effacer la dette. On explique quoi faire.
      if (err && err.error === 'credit_conflict') {
        setLoading(false);
        setError(err.detail);
        return;
      }
      // Surface a friendly message for the unique card number conflict.
      let message = err.message;
      if (err && err.card_number) {
        message = 'Ce numéro de carte est déjà attribué à un autre client.';
      } else if (err && (err.phone || err.credit_maximum)) {
        // Backend loyalty rule — surface the errors on their fields.
        setFieldErrors({
          phone: Array.isArray(err.phone) ? err.phone[0] : err.phone,
          credit_maximum: Array.isArray(err.credit_maximum) ? err.credit_maximum[0] : err.credit_maximum,
        });
        message = 'Veuillez compléter les champs obligatoires de la carte fidélité.';
      } else if (!message && err && typeof err === 'object') {
        const first = Object.values(err)[0];
        message = Array.isArray(first) ? first[0] : first;
      }
      setError(message || `Une erreur est survenue lors de ${isEditMode ? 'la mise à jour' : 'la création'} du client.`);
      setLoading(false);
    }
  };

  // Delete now lives on the edit page (moved out of the client detail popup).
  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible.')) return;
    try {
      setLoading(true);
      await customerService.deleteCustomer(id);
      navigate('/clients');
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError('Échec de la suppression du client.');
      setLoading(false);
    }
  };

  const selectedCategory = formData.category_id
    ? categories.find((c) => c.id === parseInt(formData.category_id))
    : null;
  // FIDÈLE / FIDÈLE GOLD — "fid"/"gold" match both, accent-proof (same test
  // as the backend).
  const isLoyaltyCategory = Boolean(
    selectedCategory && /fid|gold/.test((selectedCategory.name || '').toLowerCase())
  );
  // Bouton Enregistrer INACTIF tant que les champs obligatoires ne sont pas
  // remplis (owner 2026-09-09) — même règle que « Enregistrer la facture »
  // sur /facturation/add (bouton .btn-p grisé via :disabled, opacité 0.45).
  // Obligatoires : prénom ; pour un client FIDÈLE / FIDÈLE GOLD, téléphone
  // et crédit maximum > 0 (mêmes règles que handleSubmit).
  const canSave = Boolean(formData.first_name && formData.first_name.trim()) &&
    (!isLoyaltyCategory || (Boolean(formData.phone && formData.phone.trim()) && parseFloat(formData.credit_maximum) > 0));

  // Même page que « Ajouter un produit » (owner 2026-09-09) : chip de titre
  // `.tab-chip-on`, scission `.kp-split` (carte de saisie à gauche, panneau
  // collant à droite qui porte les actions), en-têtes de carte à pastille
  // grise, champs `.inp` sans icône, libellés 12px 500 encre.
  return (
    <div className="w-full px-4 pt-2 pb-4">
      <div className="mb-4 flex">
        <h1 className="tab-chip-on cursor-default">
          <IcUserPlus size={13} /> {isEditMode ? 'Modifier le client' : 'Ajouter un nouveau client'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* mt-20 = les cartes commencent 80 px sous le chip, comme /products/add. */}
        <div className="kp-split mt-20">

          {/* ================= Colonne gauche : Informations personnelles + Carte & Fidélité ================= */}
          <div className="kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
              <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcUser size={14} /></span>
              <h2 className="text-sm font-bold text-gray-900">Informations personnelles</h2>
            </div>

            <div className="kp-scroll p-4 space-y-4">
              {/* Ligne 1 — Prénom | Nom */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="first_name">Prénom <span className="req">*</span></label>
                  <input type="text" id="first_name" name="first_name" value={formData.first_name} onChange={handleChange}
                    className={inputClass} placeholder="Prénom du client" required />
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="last_name">Nom</label>
                  <input type="text" id="last_name" name="last_name" value={formData.last_name} onChange={handleChange}
                    className={inputClass} placeholder="Nom de famille du client" />
                </div>
              </div>

              {/* Ligne 2 — Email | Téléphone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                    className={inputClass} placeholder="Email du client" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="phone">
                    Téléphone {isLoyaltyCategory && <span className="req">*</span>}
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                    className={fieldErrors.phone ? inputErrClass : inputClass}
                    placeholder="Numéro de téléphone" required={isLoyaltyCategory}
                    aria-invalid={Boolean(fieldErrors.phone)} />
                  {fieldErrors.phone && (
                    <p className="form-err" role="alert"><IcAlertCircle size={12} /> {fieldErrors.phone}</p>
                  )}
                </div>
              </div>

              {/* Ligne 3 — Adresse (pleine largeur) */}
              <div>
                <label className={labelClass} htmlFor="address">Adresse</label>
                <input type="text" id="address" name="address" value={formData.address} onChange={handleChange}
                  className={inputClass} placeholder="Adresse du client" />
              </div>

              {/* ===== Carte & Fidélité — section de la même carte, séparée par un filet ===== */}
              <div className="pt-4 border-t border-gray-100 flex items-center gap-2">
                <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcCreditCard size={14} /></span>
                <h2 className="text-sm font-bold text-gray-900">Carte &amp; Fidélité</h2>
              </div>

              {/* Ligne 4 — Numéro de carte | Catégorie client */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="card_number">Numéro de carte</label>
                  <input
                    type="text"
                    id="card_number"
                    name="card_number"
                    value={formData.card_number}
                    onChange={handleChange}
                    className={`${inputClass} font-mono tracking-wider`}
                    placeholder="Ex : 1234 5678 9012"
                    autoComplete="off"
                    inputMode="numeric"
                  />
                  <p className={helpClass}>Numéro unique par client (optionnel).</p>
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="category_id">Catégorie client</label>
                  <select id="category_id" name="category_id" value={formData.category_id} onChange={handleChange} className={inputClass}>
                    <option value="">Aucune catégorie</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name} - {category.points_value}% cashback, {category.remise}% remise
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Détail de la catégorie choisie — bloc neutre du kit (gris 50, filet gris 200) */}
              {selectedCategory && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">
                  <p className="text-sm font-semibold text-gray-900">{selectedCategory.name}</p>
                  {isLoyaltyCategory && (
                    <p className="text-[11px] text-gray-500">
                      Pour cette catégorie, le <span className="font-semibold text-gray-900">téléphone</span> et le{' '}
                      <span className="font-semibold text-gray-900">crédit maximum</span> sont obligatoires.
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                      <span className="text-gray-500">Points</span>
                      <span className="font-semibold text-gray-900 tabular-nums">{selectedCategory.points_value}%</span>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                      <span className="text-gray-500">Remise</span>
                      <span className="font-semibold text-gray-900 tabular-nums">{selectedCategory.remise}%</span>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                      <span className="text-gray-500">Score</span>
                      <span className="font-semibold text-gray-900 tabular-nums">{selectedCategory.required_score} pts</span>
                    </div>
                  </div>
                  {selectedCategory.features && selectedCategory.features.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 mb-1.5">Avantages</p>
                      <div className="space-y-1">
                        {selectedCategory.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-1.5">
                            <span className="text-xs text-gray-800">{typeof feature === 'string' ? feature : feature.feature}</span>
                            {typeof feature === 'object' && feature.score !== undefined && (
                              <span className="bdg bdg-pts ml-2">{feature.score} pts</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Ligne 5 — Score de fidélité | Points */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="score">Score de fidélité</label>
                  <input type="number" id="score" name="score"
                    value={formData.score !== undefined && formData.score !== null ? formData.score : ''}
                    onChange={handleChange} min="0" step="1" className={`${inputClass} tabular-nums`} placeholder="Points de fidélité" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass} htmlFor="points">Points</label>
                  <input type="number" id="points" name="points"
                    value={formData.points !== undefined && formData.points !== null ? formData.points : ''}
                    onChange={handleChange} min="0" step="0.01" className={`${inputClass} tabular-nums`} placeholder="Points" />
                </div>
              </div>
            </div>
          </div>

          {/* ================= Colonne droite : Crédit (collante) + actions ================= */}
          <div className="kp-card kp-pane bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-shrink-0">
              <span className="h-7 w-7 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><IcCreditCard size={14} /></span>
              <h2 className="text-sm font-bold text-gray-900">Crédit</h2>
            </div>

            <div className="kp-scroll p-4 space-y-4">
              <div>
                <label className={labelClass} htmlFor="credit">Crédit actuel</label>
                <div className="relative">
                  <input type="number" id="credit" name="credit"
                    value={formData.credit !== undefined && formData.credit !== null ? formData.credit : ''}
                    onChange={handleChange} min="0" step="0.001" className={`${inputClass} pr-10 tabular-nums`} placeholder="0.000" />
                  <span className={suffixClass}>DT</span>
                </div>
              </div>
              <div>
                <label className={labelClass} htmlFor="credit_maximum">
                  Crédit maximum {isLoyaltyCategory && <span className="req">*</span>}
                </label>
                <div className="relative">
                  <input type="number" id="credit_maximum" name="credit_maximum"
                    value={formData.credit_maximum !== undefined && formData.credit_maximum !== null ? formData.credit_maximum : ''}
                    onChange={handleChange} min="0" step="0.001"
                    className={`${fieldErrors.credit_maximum ? inputErrClass : inputClass} pr-10 tabular-nums`}
                    placeholder="0.000" required={isLoyaltyCategory}
                    aria-invalid={Boolean(fieldErrors.credit_maximum)} />
                  <span className={suffixClass}>DT</span>
                </div>
                {fieldErrors.credit_maximum && (
                  <p className="form-err" role="alert"><IcAlertCircle size={12} /> {fieldErrors.credit_maximum}</p>
                )}
              </div>
            </div>

            {/* Pied — même recette que /products/add : ghost 1/3, primaire 2/3,
                Supprimer (édition, sauf le client de passage id 5) en .btn-db. */}
            <div className="p-3 border-t border-gray-100 flex-shrink-0">
              <div className="grid grid-cols-3 gap-2">
                <Link to="/clients" className="btn-g col-span-1 py-2.5 rounded-xl text-sm transition-colors">
                  Annuler
                </Link>
                <button
                  type="submit"
                  disabled={loading || !canSave}
                  title={canSave ? undefined : 'Remplissez les champs obligatoires'}
                  className="btn-p col-span-2 py-2.5 rounded-xl text-sm transition-colors"
                >
                  {loading ? (
                    <>
                      <BtnSk />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <IcSave size={14} /> {isEditMode ? 'Mettre à jour' : 'Enregistrer'}
                    </>
                  )}
                </button>
              </div>
              {isEditMode && String(id) !== '5' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="btn-db w-full mt-2 py-2.5 rounded-xl text-sm transition-colors"
                >
                  <IcTrash size={14} /> Supprimer le client
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddCustomer;
